import { html, Page, View } from 'rune-ts';
import { LayoutView } from '../../../components/cells/Layout/LayoutView';
import { footer_links } from '../../../components/cells/Footer/constant';
import { type BaseLayoutOptions } from '../../../../features/Core/service';
import { MY_PAGE_TABS_MAP, type MyPageTabsOptionMap } from '../constant';
import klass from './Recently.module.scss';
import { MyPageBody } from '../views/MyPageBody/MyPageBody';
import { MyPageEmptyScreen } from '../views/MyPageEmptyScreen/MyPageEmptyScreen';
import { ProductCardData, ProductCardOption, ProductCardSize } from '../../../../features/ProductList/type';
import type { CreatorTypeS } from '../../../../features/Creator/outbound/share';
import { CreatorCard, CreatorLikeToggleEvent } from '../../../components/cells/CreatorCard/CreatorCard';
import { ProductCard, ProductLikeToggleEvent } from '../../../components/cells/ProductCard/ProductCard';
import { MyPageApiS } from '../../../../features/MyPage/outbound/share';
import { updateUrlParams } from '../../../../shared/util';
import { MyPageTabsHookError, RenderOnRefreshHookError } from '../views/MyPageTabs/MyPageTabsError';
import { ConfigSentryF } from '../../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';

export interface RecentlyPageData {
  layout_options: BaseLayoutOptions;
  selected_tab: keyof typeof MY_PAGE_TABS_MAP;
  url: string;
  creators: CreatorTypeS.CreatorCardType[];
  products: ProductCardData[];
  adult_check_needed: boolean;
}

class RecentlyProductTab extends View<RecentlyPageData> {
  productCardList: ProductCard[];

  constructor(data: RecentlyPageData) {
    super({ ...data });

    this.productCardList = this.createProductCardList(data.products);
  }

  override template() {
    return html`
      <div class="${klass.recently_product_tab}">
        <div class="${klass.recently_product_tab_list}" data-length="${this.data.products.length}">
          ${this.productCardList}
          ${new MyPageEmptyScreen({
            title: ET('mps2::mypage::recent::empty_product_title'),
            title_desc: ET('mps2::mypage::recent::empty_product_desc'),
            description: ET('mps2::mypage::recent::empty_product_find'),
            img_src:
              '//s3.marpple.co/files/u_4078146/2025/1/original/56300f71fee21abb99eae29ebd4dd145c9ddaf6b1.png',
            visible: this.data.products.length === 0,
            uri: `/${T.lang}/@/products`,
          })}
        </div>
      </div>
    `;
  }

  protected override onRender() {
    this.delegate(ProductLikeToggleEvent, ProductCard, async (_, view) => {
      await view.toggleLikeButton();
    });
  }

  createProductCardList(products: ProductCardData[]) {
    const option: ProductCardOption = {
      resize_width: 600,
      size: ProductCardSize.medium,
    } as const;

    return products.map(
      (product: ProductCardData) =>
        new ProductCard({ ...product, adult_check_needed: this.data.adult_check_needed }, option),
    );
  }
}

class RecentlyCreatorTab extends View<RecentlyPageData> {
  override template() {
    return html`
      <div class="${klass.recently_creator_tab}">
        <div class="${klass.recently_creator_tab_list}" data-length="${this.data.creators.length}">
          ${this.data.creators.map(
            (creator) => new CreatorCard({ ...creator, adult_check_needed: this.data.adult_check_needed }),
          )}
          ${new MyPageEmptyScreen({
            title: ET('mps2::mypage::recent::empty_creator_title'),
            title_desc: ET('mps2::mypage::recent::empty_creator_desc'),
            description: ET('mps2::mypage::recent::empty_creator_find'),
            img_src:
              '//s3.marpple.co/files/u_4078146/2025/1/original/f10660bd8dcc320bb6ec8473739f36cd02aaf4042.png',
            visible: this.data.creators.length === 0,
            uri: `/${T.lang}/@/creator/ranking/realtime`,
            navigate: 'creator',
          })}
        </div>
      </div>
    `;
  }

  override onRender() {
    this.delegate(CreatorLikeToggleEvent, CreatorCard, async (_, view) => {
      await view.toggleLikeButton();
    });
  }
}

export class RecentlyBodyView extends View<RecentlyPageData> {
  my_page_tabs_option_map: Readonly<MyPageTabsOptionMap>;

  constructor(props: RecentlyPageData) {
    super(props);

    this.my_page_tabs_option_map = {
      product: {
        selected: MY_PAGE_TABS_MAP.product === this.data.selected_tab,
        value: ET('mps2::mypage::recent::product', { count: this.data.products.length }),
        view: new RecentlyProductTab(this.data),
        async renderOnRefreshHook(view: RecentlyProductTab) {
          const products = await MyPageApiS.getRecentlyViewProductsApi({ refresh: true });
          view.productCardList = view.createProductCardList(products);
        },
        postRenderOnSelectedHook() {
          history.replaceState(
            history.state,
            '',
            updateUrlParams(location.href, { tab: MY_PAGE_TABS_MAP.product }),
          );
        },
        catchOnErrorHook(myPageTabHookError?: MyPageTabsHookError) {
          if (RenderOnRefreshHookError.is(myPageTabHookError)) {
            ConfigSentryF.capture(myPageTabHookError);
          }
        },
      },
      creator: {
        selected: MY_PAGE_TABS_MAP.creator === this.data.selected_tab,
        value: ET('mps2::mypage::recent::creator', { count: this.data.creators.length }),
        view: new RecentlyCreatorTab(this.data),
        async renderOnRefreshHook(view: RecentlyCreatorTab) {
          const creators = await MyPageApiS.getRecentlyViewCreatorsApi({ refresh: true });
          view.data.creators = creators;
        },
        postRenderOnSelectedHook() {
          history.replaceState(
            history.state,
            '',
            updateUrlParams(location.href, { tab: MY_PAGE_TABS_MAP.creator }),
          );
        },
        catchOnErrorHook(myPageTabHookError?: MyPageTabsHookError) {
          if (RenderOnRefreshHookError.is(myPageTabHookError)) {
            ConfigSentryF.capture(myPageTabHookError);
          }
        },
      },
    };
  }

  override template() {
    const { selected_tab, url, layout_options } = this.data;
    const { is_mobile } = layout_options;

    return html`
      <div>
        ${new MyPageBody({
          my_page_tabs_option_map: this.my_page_tabs_option_map,
          selected_tab,
          url,
          is_mobile,
        })}
      </div>
    `;
  }
}

export class RecentlyPage extends Page<RecentlyPageData> {
  override template() {
    const { layout_options } = this.data;
    const { is_mobile } = layout_options;

    return html`
      <div class="${klass.page}">
        ${new LayoutView(
          {
            body: new RecentlyBodyView(this.data),
            footer_data: { ...footer_links, lang: ET.lang, is_mobile },
          },
          {
            require_top_arrow_button: true,
            full_width: true,
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
